<template>
	<v-dialog v-model="isOpen" hide-overlay transition="fade-transition" content-class="embedded-forms-dialog">
		<div class="embedded-forms-shell">
			<div class="embedded-forms-backdrop" @click="requestClose"></div>
			<v-card class="embedded-forms-host">
				<v-toolbar flat color="primary" dark>
					<v-toolbar-title>{{ title }}</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-chip v-if="showAuthOverlay" small label color="white" text-color="primary">Authenticating...</v-chip>
					<v-progress-circular v-else-if="!isReady" indeterminate color="white" size="20" width="2" />
					<v-btn icon dark @click="requestClose"><v-icon>close</v-icon></v-btn>
				</v-toolbar>

				<div class="embedded-forms-frame-wrap">
					<div v-if="!isReady || showAuthOverlay || authFailureMessage" class="embedded-forms-overlay">
						<template v-if="showFallbackAction">
							<v-icon color="primary" size="42" class="mb-3">mdi-shield-account-outline</v-icon>
							<div class="subheading mb-2">{{ fallbackTitle }}</div>
							<div class="body-2 grey--text text--darken-1 text-xs-center embedded-forms-overlay-copy">{{ fallbackDescription }}</div>
							<v-btn color="primary" class="mt-4" @click="triggerAuthFallback">
								Continue sign-in
							</v-btn>
						</template>
						<template v-else>
							<v-progress-circular indeterminate color="primary" size="36" width="3" class="mb-3" />
							<div class="subheading">{{ overlayText }}</div>
						</template>
					</div>

					<iframe
						ref="iframe"
						:src="iframeUrl"
						class="embedded-forms-iframe"
						frameborder="0"
						allow="clipboard-read; clipboard-write"
						@load="handleIframeLoad"
					/>
				</div>
			</v-card>
		</div>
	</v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import {
	buildEmbeddedFormsUrl,
	buildFormsAuthContext,
	getFormsAppOrigin,
	isTrustedFormsMessage,
	resolveMsalAccount,
	postMessageToFormsIframe
} from '@/common/formsEmbed';

export default {
	name: 'EmbeddedFormsAppDialog',
	props: {
		dialog: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			default: 'Forms'
		},
		path: {
			type: String,
			required: true
		},
		query: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			isOpen: this.dialog,
			isReady: false,
			showAuthOverlay: false,
			lastRoute: null,
			authFailureMessage: null,
			readyFallbackTimer: null,
			loadingFallbackTimer: null,
			showLoadingFallback: false,
			builderHasUnsavedChanges: false
		};
	},
	computed: {
		...mapGetters({
			authUser: 'auth/user',
			msalInstance: 'auth/msalInstance',
			loggedInEmployee: 'loggedInEmployee'
		}),
		iframeUrl() {
			return buildEmbeddedFormsUrl(this.path, this.query);
		},
		authContext() {
			const authContext = buildFormsAuthContext(resolveMsalAccount(this.authUser, this.msalInstance));

			if (!authContext.loginHint && this.loggedInEmployee && this.loggedInEmployee.userEmail) {
				authContext.loginHint = this.loggedInEmployee.userEmail;
			}

			if (process.env.NODE_ENV !== 'production') {
				const resolvedAccount = resolveMsalAccount(this.authUser, this.msalInstance);
				console.log('[forms-embed] resolved MSAL account', resolvedAccount);
				console.log('[forms-embed] auth context payload', authContext);
			}

			return authContext;
		},
		overlayText() {
			if (this.showAuthOverlay) return 'Signing you in to forms...';
			return 'Loading forms...';
		},
		showFallbackAction() {
			return !!this.authFailureMessage || this.showLoadingFallback;
		},
		fallbackTitle() {
			if (this.authFailureMessage) return 'Forms needs you to sign in again';
			return 'Forms is taking longer than expected';
		},
		fallbackDescription() {
			if (this.authFailureMessage) {
				return 'Continue sign-in to reconnect Forms to your current HR session.';
			}

			return 'If this does not continue automatically, you can sign in again to open Forms.';
		}
	},
	watch: {
		dialog(value) {
			this.isOpen = value;
			if (value) {
				this.resetState();
			}
		},
		isOpen(value) {
			if (!value) {
				this.$emit('close');
			}
		},
		iframeUrl() {
			this.resetState();
		}
	},
	created() {
		window.addEventListener('message', this.handleMessage);
	},
	beforeDestroy() {
		window.removeEventListener('message', this.handleMessage);
		this.clearReadyFallbackTimer();
		this.clearLoadingFallbackTimer();
	},
	methods: {
		resetState() {
			this.clearReadyFallbackTimer();
			this.clearLoadingFallbackTimer();
			this.isReady = false;
			this.showAuthOverlay = false;
			this.lastRoute = null;
			this.authFailureMessage = null;
			this.showLoadingFallback = false;
			this.builderHasUnsavedChanges = false;
		},
		handleIframeLoad() {
			this.sendAuthContext();
			this.scheduleReadyFallback();
			this.scheduleLoadingFallback();
		},
		scheduleReadyFallback() {
			this.clearReadyFallbackTimer();

			this.readyFallbackTimer = window.setTimeout(() => {
				if (!this.isReady && !this.showAuthOverlay && !this.authFailureMessage) {
					this.markReady(this.lastRoute);
				}
			}, 2500);
		},
		clearReadyFallbackTimer() {
			if (!this.readyFallbackTimer) return;
			window.clearTimeout(this.readyFallbackTimer);
			this.readyFallbackTimer = null;
		},
		scheduleLoadingFallback() {
			this.clearLoadingFallbackTimer();

			this.loadingFallbackTimer = window.setTimeout(() => {
				if (!this.isReady || this.showAuthOverlay) {
					this.showLoadingFallback = true;
				}
			}, 12000);
		},
		clearLoadingFallbackTimer() {
			if (!this.loadingFallbackTimer) return;
			window.clearTimeout(this.loadingFallbackTimer);
			this.loadingFallbackTimer = null;
		},
		markReady(payload) {
			this.clearReadyFallbackTimer();
			this.clearLoadingFallbackTimer();
			this.isReady = true;
			this.showAuthOverlay = false;
			this.lastRoute = payload || null;
			this.authFailureMessage = null;
			this.showLoadingFallback = false;
		},
		sendAuthContext() {
			postMessageToFormsIframe(this.$refs.iframe, 'forms.authContext', this.authContext, getFormsAppOrigin());
		},
		triggerAuthFallback() {
			this.showLoadingFallback = false;
			this.authFailureMessage = null;
			this.showAuthOverlay = true;
			this.sendAuthContext();
			this.scheduleLoadingFallback();
			postMessageToFormsIframe(this.$refs.iframe, 'forms.auth.loginRequested', {
				fullPath: this.lastRoute && this.lastRoute.fullPath ? this.lastRoute.fullPath : this.path
			}, getFormsAppOrigin());
		},
		async sendAuthToken(payload) {
			const requestId = payload && payload.requestId;
			if (!requestId) return;

			const responsePayload = { requestId };

			try {
				const token = await this.$store.dispatch('auth/acquireTokenSilent', {
					interactive: false,
					forceRefresh: !!payload.forceRefresh
				});

				if (!token || !token.accessToken) {
					throw new Error('No access token is available from the parent session.');
				}

				responsePayload.accessToken = token.accessToken;

				if (token.expiresOn) {
					const expiresOn = new Date(token.expiresOn).getTime();
					if (!Number.isNaN(expiresOn)) {
						responsePayload.expiresOn = expiresOn;
					}
				}
			} catch (error) {
				responsePayload.errorCode = error && error.errorCode ? error.errorCode : 'token_unavailable';
				responsePayload.message = error && error.message ? error.message : 'Unable to acquire a parent access token.';
			}

			postMessageToFormsIframe(this.$refs.iframe, 'forms.auth.tokenResponse', responsePayload, getFormsAppOrigin());
		},
		handleMessage(event) {
			if (!isTrustedFormsMessage(event, getFormsAppOrigin())) return;

			const { type, payload } = event.data;

			switch (type) {
				case 'forms.app.ready':
					this.markReady(payload);
					this.sendAuthContext();
					this.$emit('ready', payload);
					break;
				case 'forms.route.changed':
					this.markReady(payload);
					this.lastRoute = payload || null;
					this.$emit('routeChanged', payload);
					break;
				case 'forms.auth.required':
					this.markReady(this.lastRoute);
					this.sendAuthContext();
					this.$emit('authRequired', payload);
					break;
				case 'forms.auth.tokenRequested':
					this.sendAuthToken(payload);
					break;
				case 'forms.auth.loginStarted':
					this.clearReadyFallbackTimer();
					this.clearLoadingFallbackTimer();
					this.showAuthOverlay = true;
					this.authFailureMessage = null;
					this.showLoadingFallback = false;
					this.$emit('authLoginStarted', payload);
					break;
				case 'forms.auth.loginCompleted':
					this.markReady(this.lastRoute);
					this.$emit('authLoginCompleted', payload);
					break;
				case 'forms.auth.loginFailed':
					this.markReady(this.lastRoute);
					this.authFailureMessage = payload && payload.message ? payload.message : null;
					this.$emit('authLoginFailed', payload);
					break;
				case 'forms.submission.completed':
					this.showAuthOverlay = false;
					this.$emit('submissionCompleted', payload);
					break;
				case 'forms.builder.saved':
					this.showAuthOverlay = false;
					this.$emit('builderSaved', payload);
					break;
				case 'forms.builder.published':
					this.showAuthOverlay = false;
					this.$emit('builderPublished', payload);
					break;
				case 'forms.builder.close.requested':
					this.$emit('builderCloseRequested', payload);
					this.builderHasUnsavedChanges = false;
					this.requestClose();
					break;
				case 'forms.builder.dirty.changed':
					this.builderHasUnsavedChanges = !!(payload && payload.hasUnsavedChanges);
					break;
				case 'forms.close.requested':
					this.requestClose();
					break;
				default:
					break;
			}
		},
		requestClose() {
			if (this.builderHasUnsavedChanges && !window.confirm('Discard this form? Any unsaved changes will be lost.')) {
				return;
			}

			this.isOpen = false;
		}
	}
};
</script>

<style scoped>
.embedded-forms-shell {
	position: fixed;
	inset: 0;
	display: flex;
	justify-content: flex-end;
	align-items: stretch;
}

.embedded-forms-backdrop {
	position: absolute;
	inset: 0;
	background: rgba(33, 33, 33, 0.46);
}

.embedded-forms-host {
	position: relative;
	width: 72vw;
	max-width: 1100px;
	min-width: 720px;
	height: 100vh;
	display: flex;
	flex-direction: column;
	border-radius: 0;
	box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
}

.embedded-forms-frame-wrap {
	position: relative;
	flex: 1;
	min-height: 0;
}

.embedded-forms-iframe {
	width: 100%;
	height: 100%;
	border: 0;
	background: #fff;
}

.embedded-forms-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	z-index: 1;
}

.embedded-forms-overlay-copy {
	max-width: 360px;
}

@media (max-width: 1024px) {
	.embedded-forms-host {
		width: 100vw;
		max-width: 100vw;
		min-width: 0;
	}
}
</style>
